/**
 * CorrespondencePanel — the auditor's outbox for this audit.
 *
 * CorrespondenceCard (right rail, above the activity trail) lists everything
 * sent to the auditee: engagement letters, follow-ups, the report versions,
 * RCA and impact requests. The log is append-only on the server, so what is
 * shown here is the record. ComposeDialog writes to it: email a message
 * (optionally attaching the engagement letter or a frozen report version as
 * Word), or log a send that happened outside the platform. An email failure
 * (502) keeps the dialog open and offers the manual log, so the record never
 * silently drops a send.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { CORRESPONDENCE_KIND_LABELS, REPORT_KIND_LABELS } from './labels.js';

const API = process.env.REACT_APP_API_URL;

export function ComposeDialog({ auditId, initial = {}, onClose, onSent }) {
  const [kind, setKind] = useState(initial.kind || 'custom');
  const [to, setTo] = useState(initial.to || '');
  const [subject, setSubject] = useState(initial.subject || '');
  const [body, setBody] = useState(initial.body || '');
  const [attach, setAttach] = useState(initial.attach || '');
  const [reports, setReports] = useState([]);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');
  const [offerManual, setOfferManual] = useState(false);

  // Attachable report versions for the picker.
  useEffect(() => {
    (async () => {
      try {
        const r = await fetch(`${API}/api/audit/audits/${auditId}/reports`, { credentials: 'include' }).then(r => r.json());
        if (r.success) setReports(r.reports || []);
      } catch {}
    })();
  }, [auditId]);

  const post = async (manual) => {
    setBusy(manual ? 'manual' : 'send'); setErr('');
    try {
      const payload = { kind, to: to.trim(), subject: subject.trim(), body };
      if (manual) payload.manual = true;
      else if (attach) payload.attach = attach;
      const res = await fetch(`${API}/api/audit/audits/${auditId}/correspondence`, {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const d = await res.json();
      if (!d.success) {
        setErr(d.message || 'The message did not go out.');
        if (!manual) setOfferManual(true);
        setBusy('');
        return;
      }
      if (onSent) await onSent();
      onClose();
    } catch (e) {
      setErr(e.message || 'The message did not go out.');
      if (!manual) setOfferManual(true);
    }
    setBusy('');
  };

  const attachedUnknown = attach && attach.startsWith('report:') && !reports.some(r => `report:${r.id}` === attach);

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4" role="dialog" aria-label="New correspondence">
      <div className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-lg p-4 space-y-3">
        <div className="text-sm font-semibold text-slate-800">Send correspondence</div>
        <p className="text-[12px] text-slate-500">Emails the auditee and records the send on the audit's append-only correspondence log. Sent it another way? Log it instead, so the record stays complete.</p>

        <div className="grid grid-cols-2 gap-2">
          <label className="block text-xs text-slate-500">Type
            <select value={kind} onChange={e => setKind(e.target.value)} className="mt-1 w-full text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700">
              {Object.entries(CORRESPONDENCE_KIND_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="block text-xs text-slate-500">Attach
            <select value={attach} onChange={e => setAttach(e.target.value)} className="mt-1 w-full text-xs border border-slate-300 rounded-lg px-2 py-1.5 text-slate-700">
              <option value="">Nothing</option>
              <option value="letter">Engagement letter (Word)</option>
              {reports.map(r => (
                <option key={r.id} value={`report:${r.id}`}>
                  {REPORT_KIND_LABELS[r.kind] || r.kind} report ({r.issued_at ? new Date(r.issued_at).toLocaleDateString() : 'frozen version'})
                </option>
              ))}
              {attachedUnknown && <option value={attach}>Attached report (frozen version)</option>}
            </select>
          </label>
        </div>

        <label className="block text-xs text-slate-500">To
          <input value={to} onChange={e => setTo(e.target.value)} placeholder="auditee@example.com" className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-3 py-2" />
        </label>
        <label className="block text-xs text-slate-500">Subject
          <input value={subject} onChange={e => setSubject(e.target.value)} placeholder="Subject line" className="mt-1 w-full text-sm border border-slate-300 rounded-lg px-3 py-2" />
        </label>
        <label className="block text-xs text-slate-500">Message
          <textarea value={body} onChange={e => setBody(e.target.value)} rows={6} placeholder="The message to the auditee…" className="mt-1 w-full text-xs border border-slate-300 rounded-lg px-3 py-2 leading-relaxed" />
        </label>

        {err && <div className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{err}</div>}
        {offerManual && (
          <div className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
            The email did not go out. If you send it yourself, log it below so the correspondence record stays complete.
          </div>
        )}

        <div className="flex items-center gap-2 justify-end">
          <button type="button" onClick={() => post(true)} disabled={!!busy || !to.trim()} className="mr-auto px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-600 hover:bg-slate-50 disabled:opacity-50">
            {busy === 'manual' ? 'Logging…' : 'Log as sent outside'}
          </button>
          <button type="button" onClick={onClose} className="px-3 py-1.5 text-xs font-semibold rounded-lg border border-slate-300 text-slate-600">Cancel</button>
          <button type="button" onClick={() => post(false)} disabled={!!busy || !to.trim()} className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow disabled:opacity-50">
            {busy === 'send' ? 'Sending…' : 'Send'}
          </button>
        </div>
      </div>
    </div>
  );
}

export function CorrespondenceCard({ auditId, refreshKey = 0, onCompose }) {
  const [items, setItems] = useState(null); // null = never loaded

  const load = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/audit/audits/${auditId}/correspondence`, { credentials: 'include' }).then(r => r.json());
      if (r.success) setItems(r.correspondence || []);
    } catch {}
  }, [auditId]);
  useEffect(() => { load(); }, [load, refreshKey]);

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-2">
      <div className="flex items-center justify-between gap-2">
        <div className="text-sm font-semibold text-slate-800">
          Correspondence{items && items.length > 0 ? <span className="text-slate-400 font-normal"> · {items.length}</span> : null}
        </div>
        <button type="button" onClick={onCompose} className="px-2.5 py-1 text-[11px] font-semibold rounded-lg bg-gradient-to-r from-orange-500 to-orange-600 text-white shadow">New message</button>
      </div>
      <div className="text-[11px] text-slate-400">Every letter, report and request sent to the auditee, and every reply that came back to the audit's own address, on one append-only record.</div>
      {items && items.length > 0 && (
        <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
          {items.map(c => (
            <div key={c.id} className="border border-slate-100 rounded-lg px-2.5 py-1.5">
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className={`text-[10px] font-bold uppercase tracking-wide rounded px-1.5 py-0.5 border ${c.kind === 'reply' ? 'bg-sky-50 text-sky-700 border-sky-100' : 'bg-orange-50 text-orange-700 border-orange-100'}`}>
                  {c.kind === 'reply' ? 'Reply received' : (CORRESPONDENCE_KIND_LABELS[c.kind] || c.kind)}
                </span>
                <span className="text-[10px] text-slate-400">{c.kind === 'reply' ? 'by email' : c.sent_via === 'email' ? 'emailed' : 'logged'}</span>
              </div>
              {c.subject && <div className="text-[12px] font-semibold text-slate-700 mt-0.5 truncate">{c.subject}</div>}
              <div className="text-[10px] text-slate-400">
                {c.kind === 'reply' ? `from ${c.from_email || 'unknown sender'}` : `to ${c.recipient}`}{c.sent_at ? ` · ${new Date(c.sent_at).toLocaleDateString()}` : ''}
                {c.attachment_name ? ` · attached ${c.attachment_name}` : ''}
                {Array.isArray(c.attachments) && c.attachments.length ? ` · ${c.attachments.length} file${c.attachments.length === 1 ? '' : 's'} sealed` : ''}
              </div>
              {c.kind === 'reply' && c.body && <div className="mt-1 whitespace-pre-wrap text-[11px] text-slate-600 bg-slate-50 rounded-lg px-2.5 py-1.5 max-h-40 overflow-y-auto">{c.body}</div>}
            </div>
          ))}
        </div>
      )}
      {items && items.length === 0 && <div className="text-xs text-slate-400">Nothing sent yet.</div>}
    </div>
  );
}
